// Persistent per-user store of pinned-thread state.
//
// One flat set keyed by channelId for the whole user (not per-channel —
// the set IS "the list of channelIds the user has pinned to the chat
// list"). Mirrors Signal's PinnedThreadManager. Capped at 8; pinning a
// 9th evicts the oldest by `pinnedTs` FIFO.
//
// Threat model — what this store enforces alongside the encrypted envelope:
//
//   1. Sender-bound at the call site. The envelope-signature gate must
//      verify that the signing identity is the local user's BEFORE
//      handing a payload to `apply(...)`. The store does not reverify
//      the signature itself; that's the E2EE layer's job. Pin events
//      from peers that happen to decrypt successfully (group sessions,
//      forwarded envelopes) must be filtered out before reaching here.
//   2. Monotonic per `(channelId, ts)`. Strictly newer ts wins; an
//      identical ts is a no-op. A stale unpin (`payload.ts <= recordedTs`)
//      cannot undo a fresher pin, even if the server replays it.
//   3. Cap-bounded. The chat-list renderer never has to deal with more
//      than 8 entries. The 9th pin transparently evicts the oldest by
//      `pinnedTs`. Eviction is silent — the evicted thread simply
//      stops appearing in `pinnedChannelIds()` and `pinnedRecord(...)`.
//   4. Persistence. State is stored in IndexedDB under a single record
//      keyed to a fixed store key (one record per user).
//
// On-disk shape (IndexedDB record):
//   {
//     "pinned": { "ch1": PinnedThreadRecord, "ch2": PinnedThreadRecord },
//     "lastEventTs": { "ch1": 1234567890, "ch2": 1234567899 }
//   }
//
// Ported from kaether_for_ios/Stoat/E2EE/PinnedThreadStore.swift

import type { PinnedThreadPayload } from "./PinnedThread";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PinnedThreadRecord {
  readonly channelId: string;
  /** Peer-advisory Unix seconds of the pin event. */
  readonly pinnedTs: number;
  /** Local wall-clock ms when we first observed this pin. */
  readonly observedAt: number;
}

interface PinnedThreadState {
  /** Currently-pinned records, keyed by channelId. */
  pinned: Record<string, PinnedThreadRecord>;
  /** Per-channel high-water mark. Guards against out-of-order events. */
  lastEventTs: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_DB_NAME = "kaether-pinned-threads";
const STORE_NAME = "user-pins";
const DB_VERSION = 1;
/** Fixed record key — one record per user in this object store. */
const STATE_KEY = "state";

/** Maximum pinned threads per user. Beyond this, the oldest pin
 *  (smallest `pinnedTs`) is evicted FIFO. */
export const MAX_PINNED_THREADS = 8;

// ---------------------------------------------------------------------------
// DB helpers
// ---------------------------------------------------------------------------

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("IDB open blocked"));
  });
}

function idbGet(db: IDBDatabase): Promise<PinnedThreadState | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(STATE_KEY);
    tx.oncomplete = () =>
      resolve((req.result as PinnedThreadState) ?? undefined);
    tx.onerror = () => reject(tx.error);
  });
}

function idbPut(db: IDBDatabase, value: PinnedThreadState): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(value, STATE_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbDelete(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(STATE_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function emptyState(): PinnedThreadState {
  return { pinned: {}, lastEventTs: {} };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export class PinnedThreadStore {
  private readonly dbName: string;

  constructor(dbName: string = DEFAULT_DB_NAME) {
    this.dbName = dbName;
  }

  // -------------------------------------------------------------------------
  // Recording

  /**
   * Applies a pin/unpin payload with monotonic ts rules.
   *
   * Returns `true` if local state changed (a new pin appeared, an existing
   * pin was removed, or a pin was evicted by the cap).
   *
   * The caller MUST verify the envelope signature first and refuse to
   * invoke `apply(...)` for payloads not authored by the local user.
   *
   * Monotonicity gate: any event with `ts <= lastEventTs[channelId]` is
   * silently dropped. This prevents a stale unpin replayed by the server
   * from undoing a more recent pin.
   */
  async apply(
    payload: PinnedThreadPayload,
    observedAt: number = Date.now(),
  ): Promise<boolean> {
    const db = await openDb(this.dbName);
    try {
      const stored = await idbGet(db);
      let state: PinnedThreadState = stored
        ? {
            pinned: { ...stored.pinned },
            lastEventTs: { ...stored.lastEventTs },
          }
        : emptyState();

      // Monotonicity gate: discard any event whose ts is at or below the
      // high-water mark for this channelId. A stale unpin cannot undo a
      // fresher pin; an identical re-pin is a no-op.
      const lastTs = state.lastEventTs[payload.channelId];
      if (lastTs !== undefined && lastTs >= payload.ts) {
        return false;
      }

      let changed = false;

      if (payload.action === "pin") {
        const existing = state.pinned[payload.channelId];
        if (existing === undefined) {
          state.pinned[payload.channelId] = {
            channelId: payload.channelId,
            pinnedTs: payload.ts,
            observedAt,
          };
          changed = true;
        } else if (existing.pinnedTs !== payload.ts) {
          // Refresh pinnedTs to the newer value (gate above guarantees
          // payload.ts is strictly greater), but preserve observedAt —
          // that field marks first observation, not most-recent.
          state.pinned[payload.channelId] = {
            channelId: payload.channelId,
            pinnedTs: payload.ts,
            observedAt: existing.observedAt,
          };
          changed = true;
        }
        state.lastEventTs[payload.channelId] = payload.ts;

        // Cap enforcement: evict oldest by pinnedTs FIFO.
        if (Object.keys(state.pinned).length > MAX_PINNED_THREADS) {
          const records = Object.values(state.pinned);
          let oldest = records[0];
          for (const r of records) {
            if (r.pinnedTs < oldest.pinnedTs) oldest = r;
          }
          const { [oldest.channelId]: _evicted, ...rest } = state.pinned;
          state = { ...state, pinned: rest };
          // Do NOT bump lastEventTs for the evicted channel — the peer's
          // pin event was authentic, and we want a future re-pin to still
          // be processable.
          changed = true;
        }
      } else {
        // unpin
        if (state.pinned[payload.channelId] !== undefined) {
          const { [payload.channelId]: _removed, ...rest } = state.pinned;
          state = { ...state, pinned: rest };
          changed = true;
        }
        state.lastEventTs[payload.channelId] = payload.ts;
      }

      await idbPut(db, state);
      return changed;
    } finally {
      db.close();
    }
  }

  // -------------------------------------------------------------------------
  // Local-author convenience

  /**
   * Local-author pin. Persists immediately and returns the payload so
   * the caller can ship the encrypted envelope to the user's own other
   * devices.
   */
  async pin(
    channelId: string,
    ts: number = Math.floor(Date.now() / 1000),
    observedAt: number = Date.now(),
  ): Promise<PinnedThreadPayload> {
    const payload: PinnedThreadPayload = {
      schema: 1,
      channelId,
      action: "pin",
      ts,
    };
    await this.apply(payload, observedAt);
    return payload;
  }

  /** Local-author unpin. Mirror of `pin(...)`. */
  async unpin(
    channelId: string,
    ts: number = Math.floor(Date.now() / 1000),
    observedAt: number = Date.now(),
  ): Promise<PinnedThreadPayload> {
    const payload: PinnedThreadPayload = {
      schema: 1,
      channelId,
      action: "unpin",
      ts,
    };
    await this.apply(payload, observedAt);
    return payload;
  }

  // -------------------------------------------------------------------------
  // Queries

  async isPinned(channelId: string): Promise<boolean> {
    const db = await openDb(this.dbName);
    try {
      const state = await idbGet(db);
      return state !== undefined && state.pinned[channelId] !== undefined;
    } finally {
      db.close();
    }
  }

  /**
   * Returns currently-pinned channelIds, sorted by `pinnedTs` descending
   * (most-recently-pinned first). Ties are broken stably by channelId
   * ascending — deterministic convergence across all devices.
   */
  async pinnedChannelIds(): Promise<string[]> {
    const db = await openDb(this.dbName);
    try {
      const state = await idbGet(db);
      if (!state) return [];
      return Object.values(state.pinned)
        .sort((a, b) => {
          if (a.pinnedTs !== b.pinnedTs) return b.pinnedTs - a.pinnedTs;
          return a.channelId < b.channelId ? -1 : a.channelId > b.channelId ? 1 : 0;
        })
        .map((r) => r.channelId);
    } finally {
      db.close();
    }
  }

  async pinnedRecord(channelId: string): Promise<PinnedThreadRecord | undefined> {
    const db = await openDb(this.dbName);
    try {
      const state = await idbGet(db);
      return state?.pinned[channelId];
    } finally {
      db.close();
    }
  }

  // -------------------------------------------------------------------------
  // Reset

  async resetAll(): Promise<void> {
    const db = await openDb(this.dbName);
    try {
      await idbDelete(db);
    } finally {
      db.close();
    }
  }
}
